import { BorderVariants } from '@unifyapps/defs/types/interface';
import type { AppThemeConfigContextValue } from '../context/AppThemeConfigContext';

const FullySquareRadiusMap = {
  '--radius-none': '0rem',
  '--radius-xxs': '0rem',
  '--radius-xs': '0rem',
  '--radius-sm': '0rem',
  '--radius-md': '0rem',
  '--radius-lg': '0rem',
  '--radius-xl': '0rem',
  '--radius-2xl': '0rem',
  '--radius-3xl': '0rem',
  '--radius-4xl': '0rem',
  '--radius-full': '9999rem',
};

const SoftSquareRadiusMap = {
  '--radius-none': '0rem',
  '--radius-xxs': '0.125rem',
  '--radius-xs': '0.25rem',
  '--radius-sm': '0.25rem',
  '--radius-md': '0.25rem',
  '--radius-lg': '0.25rem',
  '--radius-xl': '0.25rem',
  '--radius-2xl': '0.25rem',
  '--radius-3xl': '0.625rem',
  '--radius-4xl': '0.75rem',
  '--radius-full': '9999rem',
};

const RoundedRadiusMap = {
  '--radius-none': '0rem',
  '--radius-xxs': '0.125rem',
  '--radius-xs': '0.25rem',
  '--radius-sm': '0.375rem',
  '--radius-md': '0.5rem',
  '--radius-lg': '0.5rem',
  '--radius-xl': '0.5rem',
  '--radius-2xl': '0.5rem',
  '--radius-3xl': '0.625rem',
  '--radius-4xl': '0.75rem',
  '--radius-full': '9999rem',
};

const FullyRoundedRadiusMap = {
  '--radius-none': '0rem',
  '--radius-xxs': '0.375rem',
  '--radius-xs': '0.75rem',
  '--radius-sm': '0.75rem',
  '--radius-md': '0.75rem',
  '--radius-lg': '0.75rem',
  '--radius-xl': '0.75rem',
  '--radius-2xl': '1.25rem',
  '--radius-3xl': '1.25rem',
  '--radius-4xl': '1.25rem',
  '--radius-full': '9999rem',
};

const BorderVariantsValues = {
  [BorderVariants.FullySquare]: FullySquareRadiusMap,
  [BorderVariants.SoftSquare]: SoftSquareRadiusMap,
  [BorderVariants.Rounded]: RoundedRadiusMap,
  [BorderVariants.FullyRounded]: FullyRoundedRadiusMap,
};

const ButtonRadiusAndPaddingValues = {
  [BorderVariants.FullySquare]: {
    "[data-size='xs']": {
      '--button-radius': 0,
    },
    "[data-size='sm']": {
      '--button-radius': 0,
    },
    "[data-size='md']": {
      '--button-radius': 0,
    },
    "[data-size='lg']": {
      '--button-radius': 0,
    },
  },
  [BorderVariants.SoftSquare]: {
    "[data-size='xs']": {
      '--button-radius': '0.25rem',
    },
    "[data-size='sm']": {
      '--button-radius': '0.25rem',
    },
    "[data-size='md']": {
      '--button-radius': '0.25rem',
    },
    "[data-size='lg']": {
      '--button-radius': '0.25rem',
    },
  },
  [BorderVariants.Rounded]: {
    "[data-size='xs']": {
      '--button-radius': '0.375rem',
    },
    "[data-size='sm']": {
      '--button-radius': '0.375rem',
    },
    "[data-size='md']": {
      '--button-radius': '0.5rem',
    },
    "[data-size='lg']": {
      '--button-radius': '0.5rem',
    },
  },
  [BorderVariants.FullyRounded]: {
    "[data-size='xs']": {
      '--button-radius': '9999rem',
      '--button-padding-x': '0.75rem',
    },
    "[data-size='sm']": {
      '--button-radius': '9999rem',
      '--button-padding-x': '0.75rem',
    },
    "[data-size='md']": {
      '--button-radius': '9999rem',
    },
    "[data-size='lg']": {
      '--button-radius': '9999rem',
    },
  },
};

const ChartBarRadiusValues = {
  [BorderVariants.FullySquare]: {
    '.ua-chart-root': {
      '--chart-bar-radius': 0,
    },
  },
  [BorderVariants.SoftSquare]: {
    '.ua-chart-root': {
      '--chart-bar-radius': '0.125rem',
    },
  },
  [BorderVariants.Rounded]: {
    '.ua-chart-root': {
      '--chart-bar-radius': '0.25rem',
    },
  },
  [BorderVariants.FullyRounded]: {
    '.ua-chart-root': {
      '--chart-bar-radius': '9999rem',
    },
  },
};

const InputRadiusAndPaddingValues = {
  [BorderVariants.FullySquare]: {
    ".ua-input-vars[data-size='sm']": {
      '--input-radius': 0,
    },
    ".ua-input-vars[data-size='md']": {
      '--input-radius': 0,
    },
  },
  [BorderVariants.SoftSquare]: {
    ".ua-input-vars[data-size='sm']": {
      '--input-radius': '0.25rem',
    },
    ".ua-input-vars[data-size='md']": {
      '--input-radius': '0.25rem',
    },
  },
  [BorderVariants.Rounded]: {
    ".ua-input-vars[data-size='sm']": {
      '--input-radius': '0.375rem',
    },
    ".ua-input-vars[data-size='md']": {
      '--input-radius': '0.5rem',
    },
  },
  [BorderVariants.FullyRounded]: {
    ".ua-input-vars[data-size='sm']": {
      '--input-padding-x': '0.75rem',
      '--input-radius': '1.125rem',
    },
    ".ua-input-vars[data-size='md']": {
      '--input-padding-x': '0.75rem',
      '--input-radius': '1.125rem',
    },
  },
};

const CheckboxRadiusValues = {
  [BorderVariants.FullySquare]: {},
  [BorderVariants.SoftSquare]: {},
  [BorderVariants.Rounded]: {},
  [BorderVariants.FullyRounded]: {
    ".ua-checkbox[data-size='sm']": {
      '--checkbox-radius': RoundedRadiusMap['--radius-xs'],
    },
    ".ua-checkbox[data-size='md']": {
      '--checkbox-radius': RoundedRadiusMap['--radius-sm'],
    },
  },
};

const ListPaddingValues = {
  [BorderVariants.FullySquare]: {},
  [BorderVariants.SoftSquare]: {},
  [BorderVariants.Rounded]: {},
  [BorderVariants.FullyRounded]: {
    ".ua-listitem[data-size='sm']": {
      '--ua-listitem-padding-x': '0.75rem',
    },
    ".ua-listitem[data-size='md']": {
      '--ua-listitem-padding-x': '1rem',
    },
  },
};

const TooltipRadiusValues = {
  [BorderVariants.FullySquare]: {},
  [BorderVariants.SoftSquare]: {},
  [BorderVariants.Rounded]: {},
  [BorderVariants.FullyRounded]: {
    '.ua-tooltip': {
      '--tooltip-radius': RoundedRadiusMap['--radius-sm'],
    },
  },
};

const VariableDataPillRadiusValues = {
  [BorderVariants.FullySquare]: {
    '.ua-variable-data-pill': {
      '--variable-data-pill-radius': 'var(--radius-xxs)',
    },
  },
  [BorderVariants.SoftSquare]: {
    '.ua-variable-data-pill': {
      '--variable-data-pill-radius': 'var(--radius-xxs)',
    },
  },
  [BorderVariants.Rounded]: {
    '.ua-variable-data-pill': {
      '--variable-data-pill-radius': 'var(--radius-sm)',
    },
  },
  [BorderVariants.FullyRounded]: {
    '.ua-variable-data-pill': {
      '--variable-data-pill-radius': 'var(--radius-xxs)',
    },
  },
};

function cssVariablesToString(
  variables: NonNullable<AppThemeConfigContextValue['theme']>,
  parent = '',
): string {
  return Object.entries(variables)
    .map(([key, value]) => {
      if (typeof value === 'object') {
        // Handle nested objects (e.g., `&[data-size='xs']`)
        const nestedSelector = key.startsWith('&')
          ? `${parent}${key.slice(1)}`
          : `${parent} ${key}`;
        return `${nestedSelector} { ${cssVariablesToString(value)} }`;
      }
      // Handle normal key-value pairs
      return `${key}: ${value};`;
    })
    .join(' ');
}

const getBorderStyles = (border: BorderVariants | undefined) => {
  if (!border) {
    return {};
  }

  return {
    ...BorderVariantsValues[border],
    ...ButtonRadiusAndPaddingValues[border],
    ...InputRadiusAndPaddingValues[border],
    ...CheckboxRadiusValues[border],
    ...ListPaddingValues[border],
    ...TooltipRadiusValues[border],
    ...VariableDataPillRadiusValues[border],
    ...ChartBarRadiusValues[border],
  };
};

export const getVariablesStringFromTheme = (
  theme: NonNullable<AppThemeConfigContextValue['theme']>,
) => {
  // exclude 'border' (handled separately) and 'dark' (dark scheme tokens stored as nested object)
  // to prevent invalid CSS generation from nested objects
  const { border, dark, ...rest } = theme;

  const variables = {
    ...rest,
    ...getBorderStyles(border),
  } as NonNullable<AppThemeConfigContextValue['theme']>;

  return cssVariablesToString(variables);
};
